import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { auth, db } from '../firebase';
import './Auth.css';
import { ref, set } from "firebase/database";
import AuthNavbar from './AuthNavbar';

function SignUp() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    isLongEnough: false,
    hasLower: false,
    hasUpper: false,
    hasNumberOrSymbol: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isFoodSignup = location.pathname.startsWith('/signup');

  const config = {
    title: isFoodSignup ? "Create an Account for Food" : "Create Your Account",
    subtitle: isFoodSignup ? "Order from the best local vendors." : "Let's get you started with 9troride.",
    successRedirect: isFoodSignup ? '/food-vendors' : '/',
    loginPath: isFoodSignup ? '/login' : '/ride-login',
    otherSignup: isFoodSignup
      ? { path: '/ride-signup', text: 'Sign up for Rides' }
      : { path: '/signup', text: 'Sign up for Food' }
  };

  useEffect(() => {
    const checks = {
      isLongEnough: password.length >= 8,
      hasLower: /[a-z]/.test(password),
      hasUpper: /[A-Z]/.test(password),
      hasNumberOrSymbol: /[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password),
    };
    const score = Object.values(checks).filter(Boolean).length;
    setPasswordStrength({ score, ...checks });
  }, [password]);

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');

    if (!firstName || !lastName || !email || !password || !phoneNumber) {
      setError('Please fill in all fields.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    const fullPhoneNumber = `+234${phoneNumber}`;
    if (!/^\+234[789][01]\d{8}$/.test(fullPhoneNumber)) {
      setError('Please enter a valid 10-digit Nigerian phone number (e.g. 8012345678).');
      return;
    }

    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      await updateProfile(user, {
        displayName: `${firstName} ${lastName}`
      });

      const newRider = {
        uid: user.uid,
        email: user.email,
        first_name: firstName || '',
        last_name: lastName || '',
        phoneNumber: fullPhoneNumber,
        created_at: Date.now(),
        agreementsAccepted: true,
        isEmailVerified: user.emailVerified,
        password_created: true,
        status: 'offline',
        notification_settings: {
          newsletters_email: true,
          promotions_email: true,
          promotions_push: true,
          receipts_email: true,
          rides_push: true,
          system_notifications_enabled: true,
          system_push: true
        },
        safety_settings: {
          audio_recording: false,
          pin_verification: true,
          ride_check: false,
          ride_pin: Math.floor(1000 + Math.random() * 9000).toString()
        },
      };

      await set(ref(db, 'riders/' + user.uid), newRider);

      console.log('Signed up user and created rider entry:', user);
      navigate(config.successRedirect);
    } catch (error) {
      setError(error.message.replace('Firebase: ', ''));
      console.error("Error signing up:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper">
      <AuthNavbar />
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <h1>{config.title}</h1>
            <p>{config.subtitle}</p>
          </div>

          <form onSubmit={handleSignUp} className="auth-form">
            {error && <p className="auth-error">{error}</p>}
            <div className="auth-form-row">
              <div className="auth-input-group">
                <label htmlFor="firstName">First Name</label>
                <div className="auth-input-wrapper">
                  <input
                    type="text"
                    id="firstName"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                  <div className="auth-input-icons">
                    {firstName && (
                      <button type="button" className="auth-input-icon-btn" onClick={() => setFirstName('')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="auth-input-group">
                <label htmlFor="lastName">Last Name</label>
                <div className="auth-input-wrapper">
                  <input
                    type="text"
                    id="lastName"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                  <div className="auth-input-icons">
                    {lastName && (
                      <button type="button" className="auth-input-icon-btn" onClick={() => setLastName('')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="auth-form-row">
              <div className="auth-input-group">
                <label htmlFor="phoneNumber">Phone Number</label>
                <div className="auth-input-wrapper phone-input">
                  <span className="phone-prefix">+234</span>
                  <input
                    type="tel"
                    id="phoneNumber"
                    placeholder="8012345678"
                    value={phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Allow only digits
                      if (value.length <= 10) {
                        setPhoneNumber(value);
                      }
                    }}
                    required
                  />
                  <div className="auth-input-icons">
                    {phoneNumber && (
                      <button type="button" className="auth-input-icon-btn" onClick={() => setPhoneNumber('')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="auth-input-group">
                <label htmlFor="email">Email Address</label>
                <div className="auth-input-wrapper">
                  <input
                    type="email"
                    id="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <div className="auth-input-icons">
                    {email && (
                      <button type="button" className="auth-input-icon-btn" onClick={() => setEmail('')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="auth-input-group">
              <label htmlFor="password">Password</label>
              <div className="auth-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <div className="auth-input-icons">
                  {password && (
                    <button type="button" className="auth-input-icon-btn" onClick={() => setPassword('')}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                  )}
                  <button type="button" className="auth-input-icon-btn" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                    )}
                  </button>
                </div>
              </div>
              {password && (
                <div className="password-strength-indicator">
                  <div className="strength-bar">
                    <div className={`strength-segment ${passwordStrength.score >= 1 ? 'level-' + passwordStrength.score : ''}`}></div>
                    <div className={`strength-segment ${passwordStrength.score >= 2 ? 'level-' + passwordStrength.score : ''}`}></div>
                    <div className={`strength-segment ${passwordStrength.score >= 3 ? 'level-' + passwordStrength.score : ''}`}></div>
                    <div className={`strength-segment ${passwordStrength.score >= 4 ? 'level-' + passwordStrength.score : ''}`}></div>
                  </div>
                  <ul className="strength-criteria">
                    <li className={passwordStrength.isLongEnough ? 'met' : ''}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/></svg>
                      <span>At least 8 characters</span>
                    </li>
                    <li className={passwordStrength.hasLower ? 'met' : ''}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/></svg>
                      <span>A lowercase letter</span>
                    </li>
                    <li className={passwordStrength.hasUpper ? 'met' : ''}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/></svg>
                      <span>An uppercase letter</span>
                    </li>
                    <li className={passwordStrength.hasNumberOrSymbol ? 'met' : ''}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/></svg>
                      <span>A number or symbol</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>
            <p className="auth-terms">
              By signing up, you agree to our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
            </p>
            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? 'Signing Up...' : 'Sign Up'}
            </button>
          </form>

            <div className="auth-redirect">
              <p>Already have an account? <Link to={config.loginPath} state={{ from: location.state?.from }}>Log in</Link></p>
              <p style={{ marginTop: '12px' }}><Link to={config.otherSignup.path} state={{ from: location.state?.from }}>{config.otherSignup.text}</Link></p>
            </div>
        </div>
        </div>
        <div className="auth-footer-art"></div>
      </div>
  );
}

export default SignUp;